import type { History } from 'history';
import type { QueryClient, UseMutateFunction } from 'react-query';
import {
  handleErrorToast,
  handleSuccessToast,
} from 'src/components/molecules/Toast/Toast';
import { buildDuplicateInput } from 'src/components/pages/CoiFormManagement/buildDuplicateInput';
import type {
  DuplicateFormManagementQuestionnaireMutation,
  DuplicateFormManagementQuestionnaireMutationVariables,
  UpdateFormManagementQuestionnaireStatusMutation,
  UpdateFormManagementQuestionnaireStatusMutationVariables,
} from 'src/graphql';
import { QuestionnaireStatus } from 'src/graphql';

export function runDuplicateFormManagementQuestionnaire(options: {
  duplicateQuestionnaire: UseMutateFunction<
    DuplicateFormManagementQuestionnaireMutation,
    unknown,
    DuplicateFormManagementQuestionnaireMutationVariables,
    unknown
  >;
  questionnaireId: string;
  publicTitle: string;
  mode: 'all-languages' | 'default-only';
  setShowDuplicateModal: (open: boolean) => void;
}): void {
  const {
    duplicateQuestionnaire,
    questionnaireId,
    publicTitle,
    mode,
    setShowDuplicateModal,
  } = options;

  duplicateQuestionnaire(
    {
      id: questionnaireId,
      input: buildDuplicateInput(publicTitle, mode),
    },
    {
      onSuccess: () => {
        setShowDuplicateModal(false);
        handleSuccessToast({
          toastMessage: 'Form duplicated',
          toastDescription: `"${publicTitle}" has been duplicated.`,
        });
      },
      onError: () => {
        setShowDuplicateModal(false);
        handleErrorToast({
          toastMessage: 'Failed to duplicate form',
          toastDescription:
            'Something went wrong while duplicating the form. Please try again.',
        });
      },
    },
  );
}

export function runArchiveFormManagementQuestionnaire(options: {
  updateQuestionnaireStatus: UseMutateFunction<
    UpdateFormManagementQuestionnaireStatusMutation,
    unknown,
    UpdateFormManagementQuestionnaireStatusMutationVariables,
    unknown
  >;
  queryClient: QueryClient;
  questionnaireId: string;
  publicTitle: string;
  setShowArchiveModal: (open: boolean) => void;
  setPreventNavigation: (prevent: boolean) => void;
  history: History;
  returnTo: string;
}): void {
  const {
    updateQuestionnaireStatus,
    queryClient,
    questionnaireId,
    publicTitle,
    setShowArchiveModal,
    setPreventNavigation,
    history,
    returnTo,
  } = options;

  updateQuestionnaireStatus(
    {
      id: questionnaireId,
      input: { status: QuestionnaireStatus.Archived },
    },
    {
      onSuccess: () => {
        setShowArchiveModal(false);
        handleSuccessToast({
          toastMessage: 'Form archived',
          toastDescription: `"${publicTitle}" has been archived.`,
        });
        queryClient.invalidateQueries(['FormManagementQuestionnaires']);
        setPreventNavigation(false);
        history.push(returnTo);
      },
      onError: () => {
        setShowArchiveModal(false);
        handleErrorToast({
          toastMessage: 'Failed to archive form',
          toastDescription:
            'Something went wrong while archiving the form. Please try again.',
        });
      },
    },
  );
}
